'use client';

import { Container } from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';
import { DecorativeLines } from '@/components/ui/decorative-lines';
import { Image } from '@/components/ui/image/image';
import type { MediaData } from '@/lib/getMediaData';
import { Slide } from '@/components/experience/slider/Slide';
import { Slider } from '@/components/experience/slider/Slider';
import { AgendaSection } from '@/components/section/agenda-section/agenda-section';
import type { AgendaCardData } from '@/components/section/agenda-section/agenda-card';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import { cn } from '@/lib/utils';
import type { SectionBgVariant } from '@/lib/types/misc';
import type { Document } from '@contentful/rich-text-types';
import type { ReactNode, HTMLAttributes } from 'react';
import styles from './slider-section.module.scss';

export type SliderSectionSlide = {
  id: string;
  heading?: string;
  description?: Document;
  imageUrl?: string | null;
  stars?: string | null;
  mediaData?: MediaData;
  time?: string;
  speakerName?: string;
  speakerRole?: string;
  speakerPhotoUrl?: string | null;
};

export type SliderSectionProps = {
  sectionId: string;
  badge?: string;
  showBadgeLines?: boolean;
  title: string;
  description?: Document;
  slides: SliderSectionSlide[];
  displayAs?: 'slider' | 'agenda';
  cta?: ReactNode;
  autoscrollSpeed?: number;
  autoplay?: boolean;
  controls?: boolean;
  featuredMove?: string;
  border?: boolean;
  borderRadius?: boolean;
  slideBackground?: string;
  shadow?: boolean;
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string | null;
  backgroundColorFrom?: string;
  isBackgroundDark?: boolean;
  showDecorativeLines?: boolean;
  showDecorativeDots?: boolean;
  className?: string;
} & Omit<HTMLAttributes<HTMLElement>, 'title'>;

export const SliderSection = ({
  sectionId,
  badge,
  showBadgeLines = false,
  title,
  description,
  slides,
  displayAs = 'slider',
  cta,
  autoscrollSpeed,
  autoplay = true,
  controls = true,
  featuredMove = 'moveDown',
  border = true,
  borderRadius = true,
  slideBackground = '_bg-white',
  shadow = false,
  backgroundVariant = 'white',
  backgroundImage,
  backgroundColorFrom,
  isBackgroundDark = false,
  showDecorativeLines = false,
  showDecorativeDots = false,
  className,
  ...props
}: SliderSectionProps) => {
  if (displayAs === 'agenda') {
    const agendaCards: AgendaCardData[] = slides.map((slide) => ({
      id: slide.id,
      heading: slide.heading ?? '',
      time: slide.time,
      subheading: slide.description,
      imageUrl: slide.imageUrl,
      speakerPhotoUrl: slide.speakerPhotoUrl,
      speakerName: slide.speakerName,
      speakerRole: slide.speakerRole,
    }));

    return (
      <AgendaSection
        sectionId={sectionId}
        badge={badge}
        title={title}
        description={description}
        cta={cta}
        cards={agendaCards}
        enableAutoplay={autoplay}
        controls={controls}
        autoscrollSpeed={autoscrollSpeed}
        backgroundVariant={backgroundVariant}
        backgroundImage={backgroundImage}
        backgroundColorFrom={backgroundColorFrom}
        isBackgroundDark={isBackgroundDark}
        showDecorativeLines={showDecorativeLines}
        showDecorativeDots={showDecorativeDots}
        className={className}
        {...props}
      />
    );
  }

  const inlineStyle =
    backgroundVariant === 'custom' && backgroundColorFrom
      ? { backgroundColor: backgroundColorFrom }
      : undefined;

  return (
    <section
      id={sectionId}
      className={cn(styles.component, styles[`component--bg-${backgroundVariant}`], className)}
      style={inlineStyle}
      {...props}
    >
      {(showDecorativeLines || showDecorativeDots) && (
        <DecorativeLines
          showLines={showDecorativeLines}
          showDots={showDecorativeDots}
          variant={isBackgroundDark ? 'dark' : 'light'}
        />
      )}

      {backgroundVariant === 'image' && backgroundImage && (
        <div className={styles.bg_image} aria-hidden>
          <Image src={backgroundImage} alt="" fill style={{ objectFit: 'cover' }} sizes="100vw" />
        </div>
      )}

      <Container className={styles.container}>
        <div className={styles.header}>
          {badge && (
            <div className={styles.badge_row}>
              {showBadgeLines && <div className={styles.badge_line_left} aria-hidden />}
              <Badge text={badge} />
              {showBadgeLines && <div className={styles.badge_line_right} aria-hidden />}
            </div>
          )}
          <div className={styles.header_text}>
            <h2
              className={cn(
                styles.title,
                isBackgroundDark ? styles['title--dark'] : styles['title--light'],
              )}
            >
              {title}
            </h2>
            {description && (
              <div
                className={cn(
                  styles.description,
                  isBackgroundDark ? styles['description--dark'] : styles['description--light'],
                )}
              >
                <RichTextViewer content={description} />
              </div>
            )}
          </div>
          {cta && <div className={styles.header_cta}>{cta}</div>}
        </div>

        <div
          className={cn(styles.slider_wrapper, slides.length < 3 && styles.slider_wrapper_centered)}
        >
          {slides.length < 3 ? (
            slides.map((slide) => {
              const slideMedia: MediaData | undefined =
                slide.mediaData ??
                (slide.imageUrl
                  ? {
                      mediaType: 'Advanced Image',
                      src: slide.imageUrl,
                      alt: slide.heading ?? '',
                      url: null,
                      width: null,
                      height: null,
                      videoEmbedUrl: null,
                      animationFormat: null,
                      animationSrc: null,
                      fallbackSrc: null,
                      loop: false,
                      autoplay: false,
                      playAudio: false,
                      lottieJson: null,
                      embedCode: null,
                      fileSrc: null,
                      fileName: null,
                      fileSize: null,
                      fileType: null,
                      description: null,
                    }
                  : undefined);

              return (
                <Slide key={slide.id} stars={slide.stars ?? undefined} media={slideMedia}>
                  {(slide.description || slide.heading) && (
                    <div className={styles.slide_content}>
                      {slide.description && (
                        <div className={styles.slide_description}>
                          <RichTextViewer content={slide.description} />
                        </div>
                      )}
                      {slide.heading && <p className={styles.slide_heading}>{slide.heading}</p>}
                    </div>
                  )}
                </Slide>
              );
            })
          ) : (
            <Slider
              autoplay={autoplay}
              controls={controls}
              featuredMove={featuredMove}
              border={border}
              borderRadius={borderRadius}
              slideBackground={slideBackground}
              shadow={shadow}
            >
              {slides.map((slide) => {
                const slideMedia: MediaData | undefined =
                  slide.mediaData ??
                  (slide.imageUrl
                    ? {
                        mediaType: 'Advanced Image',
                        src: slide.imageUrl,
                        alt: slide.heading ?? '',
                        url: null,
                        width: null,
                        height: null,
                        videoEmbedUrl: null,
                        animationFormat: null,
                        animationSrc: null,
                        fallbackSrc: null,
                        loop: false,
                        autoplay: false,
                        playAudio: false,
                        lottieJson: null,
                        embedCode: null,
                        fileSrc: null,
                        fileName: null,
                        fileSize: null,
                        fileType: null,
                        description: null,
                      }
                    : undefined);

                return (
                  <Slide key={slide.id} stars={slide.stars ?? undefined} media={slideMedia}>
                    {(slide.description || slide.heading) && (
                      <div className={styles.slide_content}>
                        {slide.description && (
                          <div className={styles.slide_description}>
                            <RichTextViewer content={slide.description} />
                          </div>
                        )}
                        {slide.heading && <p className={styles.slide_heading}>{slide.heading}</p>}
                      </div>
                    )}
                  </Slide>
                );
              })}
            </Slider>
          )}
        </div>
      </Container>
    </section>
  );
};
